import { defineStore } from "pinia";
import { computed, ref } from "vue";
import axios from "axios";

import { LabelTemplateDestinationTypeTranslation } from "@/api/classes/labelTemplate/labelTemplateTranslates";
import LABEL_TEMPLATE_API from "@/api/classes/labelTemplate/label-template_api";
import Product from "@/api/classes/product/product";
import {
  LabelTemplateDestinationType,
  type LabelTemplateDTO,
} from "@/api/classes/labelTemplate/labelTemplate.dto";

import type { TableColumnTypeCustom } from "@/types/claims";
import type { BaseProductDto } from "@/types/product/productTypes";

import { initDefaultColumns } from "@/composables/table/tableColumns";

import { createNotification } from "@/tools/notification";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import { initialFilter } from "@/stores/product/products/productsTable";
import type { LabelTemplate } from "@/stores/labelTemplate/models/label-template";
import type { MarketPlace } from "@/types/market-place";
import { useUser } from "@/stores/user/user";

export interface PrintModalProduct {
  id: string;
  size: string;
  barcodes: Partial<Record<MarketPlace, string[] | null>>;
  image: string;
  sellerArticle?: string | number;
  baseProduct: BaseProductDto;
}

export interface LabelTemplateTableData {
  destinationType: string;
  labelTemplateName: string;
  labelSizeInMm: string;
  attachedPrinterName: string;
  actions: null;
  label: LabelTemplateDTO;
}

export const useLabelTemplateStore = defineStore("labelTemplateStore", () => {
  const userStore = useUser();
  const currentEditingLabelClass = ref<LabelTemplate | null>(null);

  const getAllPersonalLabelTemplatesStatus = ref(enumStatusLoad.NOT_STARTED);
  const getPersonalLabelTemplateByIdStatus = ref(enumStatusLoad.NOT_STARTED);
  const deletePersonalLabelTemplateByIdStatus = ref(enumStatusLoad.NOT_STARTED);
  const createPersonalLabelTemplateStatus = ref(enumStatusLoad.NOT_STARTED);
  const updatePersonalLabelTemplateStatus = ref(enumStatusLoad.NOT_STARTED);

  const getAllCommonLabelTemplatesStatus = ref(enumStatusLoad.NOT_STARTED);
  const getCommonLabelTemplateByIdStatus = ref(enumStatusLoad.NOT_STARTED);
  const deleteCommonLabelTemplateByIdStatus = ref(enumStatusLoad.NOT_STARTED);
  const createCommonLabelTemplateStatus = ref(enumStatusLoad.NOT_STARTED);
  const updateCommonLabelTemplateStatus = ref(enumStatusLoad.NOT_STARTED);

  const setDefaultLabelTemplateStatus = ref(enumStatusLoad.NOT_STARTED);
  const updateLabelTemplatePrinterStatus = ref(enumStatusLoad.NOT_STARTED);
  const getProductsForPreviewOptionsStatus = ref(enumStatusLoad.NOT_STARTED);

  const allPersonalLabels = ref<LabelTemplateDTO[]>([]);
  const allCommonLabels = ref<LabelTemplateDTO[]>([]);

  const personalTableData = ref<LabelTemplateTableData[]>([]);
  const commonTableData = ref<LabelTemplateTableData[]>([]);

  const processingPersonalLabels = ref<Set<LabelTemplateDTO["id"]>>(new Set());
  const processingCommonLabels = ref<Set<LabelTemplateDTO["id"]>>(new Set());

  const productForPreviewMap = ref<Map<BaseProductDto["id"], BaseProductDto>>(
    new Map(),
  );

  const adminEmail = "ps@lets-work.ru";

  const isAdminAccount = computed(() => {
    return userStore.user?.login === adminEmail;
  });

  const productsForPreviewOptions = ref<
    { value: BaseProductDto["id"]; label: BaseProductDto["title"] }[]
  >([]);

  const personalTableColumns = ref<
    TableColumnTypeCustom<LabelTemplateTableData>[]
  >(
    initDefaultColumns(
      [
        { title: "Тип", dataIndex: "destinationType" },
        { title: "Название", dataIndex: "labelTemplateName" },
        { title: "Размер, мм", dataIndex: "labelSizeInMm" },
        { title: "Принтер", dataIndex: "attachedPrinterName" },
        { title: "Действия", dataIndex: "actions", maxWidth: 50 },
      ],
      { minWidth: 150 },
    ),
  );

  const commonTableColumns = ref<
    TableColumnTypeCustom<LabelTemplateTableData>[]
  >(
    initDefaultColumns(
      [
        { title: "Тип", dataIndex: "destinationType" },
        { title: "Название", dataIndex: "labelTemplateName" },
        { title: "Размер, мм", dataIndex: "labelSizeInMm" },
        { title: "Действия", dataIndex: "actions", maxWidth: 50 },
      ],
      { minWidth: 150 },
    ),
  );

  const getAllPersonalLabelTemplates = async () => {
    processingPersonalLabels.value.clear();
    getAllPersonalLabelTemplatesStatus.value = enumStatusLoad.LOADING;

    try {
      const { data } = await LABEL_TEMPLATE_API.getAllPersonalLabelTemplates();

      getAllPersonalLabelTemplatesStatus.value = enumStatusLoad.LOADED;
      allPersonalLabels.value = data;
      personalTableData.value = createTableData(data);

      return data;
    } catch (error) {
      onError(error);
      getAllPersonalLabelTemplatesStatus.value = enumStatusLoad.ERROR;
    }
  };

  const getAllCommonLabelTemplates = async () => {
    processingCommonLabels.value.clear();
    getAllCommonLabelTemplatesStatus.value = enumStatusLoad.LOADING;

    try {
      const { data } = await LABEL_TEMPLATE_API.getAllCommonLabelTemplates();

      getAllCommonLabelTemplatesStatus.value = enumStatusLoad.LOADED;
      allCommonLabels.value = data;
      commonTableData.value = createTableData(data);

      return data;
    } catch (error) {
      onError(error);
      getAllCommonLabelTemplatesStatus.value = enumStatusLoad.ERROR;
    }
  };

  const getPersonalLabelTemplateById = async (
    labelId: LabelTemplateDTO["id"],
  ) => {
    getPersonalLabelTemplateByIdStatus.value = enumStatusLoad.LOADING;

    try {
      const response =
        await LABEL_TEMPLATE_API.getPersonalLabelTemplateById(labelId);

      getPersonalLabelTemplateByIdStatus.value = enumStatusLoad.LOADED;

      return response.data;
    } catch (error) {
      onError(error);
      getPersonalLabelTemplateByIdStatus.value = enumStatusLoad.ERROR;
    }
  };

  const getCommonLabelTemplateById = async (
    labelId: LabelTemplateDTO["id"],
  ) => {
    getCommonLabelTemplateByIdStatus.value = enumStatusLoad.LOADING;

    try {
      const response =
        await LABEL_TEMPLATE_API.getCommonLabelTemplateById(labelId);

      getCommonLabelTemplateByIdStatus.value = enumStatusLoad.LOADED;

      return response.data;
    } catch (error) {
      onError(error);
      getCommonLabelTemplateByIdStatus.value = enumStatusLoad.ERROR;
    }
  };

  const deletePersonalLabelTemplateById = async (
    id: LabelTemplateDTO["id"],
  ) => {
    deletePersonalLabelTemplateByIdStatus.value = enumStatusLoad.LOADING;
    processingPersonalLabels.value.add(id);

    try {
      await LABEL_TEMPLATE_API.deletePersonalLabelTemplateById(id);

      deletePersonalLabelTemplateByIdStatus.value = enumStatusLoad.LOADED;

      createNotification("success", "Успех!", "Этикетка успешно удалена");

      await getAllPersonalLabelTemplates();
    } catch (error) {
      deletePersonalLabelTemplateByIdStatus.value = enumStatusLoad.ERROR;
      onError(error, "Не удалось удалить этикету ");
    }

    processingPersonalLabels.value.delete(id);
  };

  const deleteCommonLabelTemplates = async (ids: LabelTemplateDTO["id"][]) => {
    for (const id of ids) {
      processingCommonLabels.value.add(id);
    }

    deleteCommonLabelTemplateByIdStatus.value = enumStatusLoad.LOADING;

    try {
      await LABEL_TEMPLATE_API.deleteCommonLabelTemplates(ids);

      deleteCommonLabelTemplateByIdStatus.value = enumStatusLoad.LOADED;

      createNotification("success", "Успех!", "Этикетка успешно удалена");

      await getAllCommonLabelTemplates();
    } catch (error) {
      deleteCommonLabelTemplateByIdStatus.value = enumStatusLoad.ERROR;
      onError(error, "Не удалось удалить этикету ");
    }

    for (const id of ids) {
      processingCommonLabels.value.delete(id);
    }
  };

  const createPersonalLabelTemplate = async (
    labelTemplate: LabelTemplateDTO,
  ) => {
    createPersonalLabelTemplateStatus.value = enumStatusLoad.LOADING;

    try {
      await LABEL_TEMPLATE_API.createPersonalLabelTemplate(labelTemplate);

      createPersonalLabelTemplateStatus.value = enumStatusLoad.LOADED;
      createNotification(
        "success",
        `Этикетка "${labelTemplate.labelTemplateName}" создана`,
      );

      return true;
    } catch (error) {
      onError(
        error,
        `Не удалось создать этикету "${labelTemplate.labelTemplateName}"`,
      );
      createPersonalLabelTemplateStatus.value = enumStatusLoad.ERROR;

      return false;
    }
  };

  const createCommonLabelTemplate = async (labelTemplate: LabelTemplateDTO) => {
    createCommonLabelTemplateStatus.value = enumStatusLoad.LOADING;

    try {
      await LABEL_TEMPLATE_API.createCommonLabelTemplate(labelTemplate);

      createCommonLabelTemplateStatus.value = enumStatusLoad.LOADED;
      createNotification(
        "success",
        `Этикетка "${labelTemplate.labelTemplateName}" создана`,
      );

      return true;
    } catch (error) {
      onError(
        error,
        `Не удалось создать этикету "${labelTemplate.labelTemplateName}"`,
      );
      createCommonLabelTemplateStatus.value = enumStatusLoad.ERROR;

      return false;
    }
  };

  const updatePersonalLabelTemplate = async (
    labelTemplate: LabelTemplateDTO,
  ) => {
    processingPersonalLabels.value.add(labelTemplate.id);
    updatePersonalLabelTemplateStatus.value = enumStatusLoad.LOADING;

    try {
      await LABEL_TEMPLATE_API.updatePersonalLabelTemplate(labelTemplate);

      updatePersonalLabelTemplateStatus.value = enumStatusLoad.LOADED;

      createNotification(
        "success",
        `Этикетка "${labelTemplate.labelTemplateName}" обновлена`,
      );

      return true;
    } catch (error) {
      updatePersonalLabelTemplateStatus.value = enumStatusLoad.ERROR;
      onError(
        error,
        `Не удалось обновить этикетку "${labelTemplate.labelTemplateName}"`,
      );
    }

    processingPersonalLabels.value.delete(labelTemplate.id);
  };

  const updateCommonLabelTemplate = async (labelTemplate: LabelTemplateDTO) => {
    processingCommonLabels.value.add(labelTemplate.id);
    updateCommonLabelTemplateStatus.value = enumStatusLoad.LOADING;

    try {
      await LABEL_TEMPLATE_API.updateCommonLabelTemplate(labelTemplate);

      updateCommonLabelTemplateStatus.value = enumStatusLoad.LOADED;

      createNotification(
        "success",
        `Этикетка "${labelTemplate.labelTemplateName}" обновлена`,
      );

      return true;
    } catch (error) {
      updateCommonLabelTemplateStatus.value = enumStatusLoad.ERROR;
      onError(
        error,
        `Не удалось обновить этикетку "${labelTemplate.labelTemplateName}"`,
      );
    }

    processingCommonLabels.value.delete(labelTemplate.id);
  };

  const setDefaultLabelTemplate = async (
    labelTemplateId: LabelTemplateDTO["id"],
  ) => {
    processingPersonalLabels.value.add(labelTemplateId);
    setDefaultLabelTemplateStatus.value = enumStatusLoad.LOADING;

    try {
      await LABEL_TEMPLATE_API.setDefaultLabelTemplate(labelTemplateId);
      await getAllPersonalLabelTemplates();

      setDefaultLabelTemplateStatus.value = enumStatusLoad.LOADED;
    } catch (error) {
      onError(error);
      setDefaultLabelTemplateStatus.value = enumStatusLoad.ERROR;
    }

    processingPersonalLabels.value.delete(labelTemplateId);
  };

  const updateLabelTemplatePrinter = async (
    id: LabelTemplateDTO["id"],
    printer: string | null,
  ) => {
    updateLabelTemplatePrinterStatus.value = enumStatusLoad.LOADING;
    processingPersonalLabels.value.add(id);

    try {
      await LABEL_TEMPLATE_API.updateLabelTemplatePrinter(id, printer);
      await getAllPersonalLabelTemplates();

      updateLabelTemplatePrinterStatus.value = enumStatusLoad.LOADED;
      createNotification(
        "success",
        "Успех!",
        "Принтер успешно обновлен для этикетки.",
      );

      processingPersonalLabels.value.delete(id);
      return true;
    } catch (error: unknown) {
      onError(error);
      processingPersonalLabels.value.delete(id);
      updateLabelTemplatePrinterStatus.value = enumStatusLoad.ERROR;
      return false;
    }
  };

  const createTableData = (
    data: LabelTemplateDTO[],
  ): LabelTemplateTableData[] => {
    return data.map((label) => {
      const {
        destinationType,
        labelTemplateName,
        labelWidthInMm,
        labelHeightInMm,
        attachedPrinterName,
      } = label;

      return {
        destinationType:
          LabelTemplateDestinationTypeTranslation[destinationType],
        labelTemplateName: labelTemplateName,
        labelSizeInMm: `${labelWidthInMm}x${labelHeightInMm}`,
        attachedPrinterName:
          attachedPrinterName && attachedPrinterName !== "null"
            ? attachedPrinterName
            : "Не указан",
        actions: null,
        label,
        permission: null,
      };
    });
  };

  const getProductsForPreviewOptions = async (searchPhrase?: string) => {
    if (getProductsForPreviewOptionsStatus.value === enumStatusLoad.LOADING)
      return;

    getProductsForPreviewOptionsStatus.value = enumStatusLoad.LOADING;
    const params = { ...initialFilter };

    if (searchPhrase) {
      params.searchPhrase = searchPhrase;
    }

    const { data } = await Product.getProductsForTable(10, 0, params);
    const products = data.item1;

    productsForPreviewOptions.value = Object.values(products).reduce<
      { value: BaseProductDto["id"]; label: BaseProductDto["title"] }[]
    >((total, productsArray) => {
      const processedArray = productsArray.map((product) => {
        productForPreviewMap.value.set(product.id, product);

        return {
          value: product.id,
          label: product.title,
        };
      });

      total = [...total, ...processedArray];

      return total;
    }, []);

    getProductsForPreviewOptionsStatus.value = enumStatusLoad.LOADED;
  };

  const getDefaultLabel = (destinationType: LabelTemplateDestinationType) => {
    const labelsWithThisDestinationType = allPersonalLabels.value.filter(
      (label) => label.destinationType === destinationType,
    );

    if (!labelsWithThisDestinationType.length) {
      createNotification(
        "error",
        `Нет этикеток с назначением ${LabelTemplateDestinationTypeTranslation[destinationType]}`,
      );

      return null;
    }

    let defaultLabel = labelsWithThisDestinationType.find(
      (label) => label.isDefault,
    );

    if (!defaultLabel) {
      defaultLabel = labelsWithThisDestinationType[0];
    }

    return defaultLabel;
  };

  const onError = (err: unknown, customMessage?: string) => {
    if (axios.isAxiosError(err)) {
      console.log(err);
      createNotification(
        "error",
        customMessage || err.response?.data?.detail || err.response?.data,
        undefined,
        "topRight",
      );
    }
  };

  return {
    getAllPersonalLabelTemplatesStatus,
    getPersonalLabelTemplateByIdStatus,
    deletePersonalLabelTemplateByIdStatus,
    getProductsForPreviewOptionsStatus,
    createPersonalLabelTemplateStatus,
    updatePersonalLabelTemplateStatus,
    setDefaultLabelTemplateStatus,
    updateLabelTemplatePrinterStatus,
    allPersonalLabels,
    processingPersonalLabels,
    personalTableData,
    personalTableColumns,
    productsForPreviewOptions,
    productForPreviewMap,
    getProductsForPreviewOptions,
    getAllPersonalLabelTemplates,
    getPersonalLabelTemplateById,
    deletePersonalLabelTemplateById,
    createPersonalLabelTemplate,
    updatePersonalLabelTemplate,
    setDefaultLabelTemplate,
    updateLabelTemplatePrinter,
    getDefaultLabel,

    isAdminAccount,
    currentEditingLabelClass,

    getAllCommonLabelTemplatesStatus,
    getCommonLabelTemplateByIdStatus,
    deleteCommonLabelTemplateByIdStatus,
    createCommonLabelTemplateStatus,
    updateCommonLabelTemplateStatus,
    allCommonLabels,
    commonTableData,
    commonTableColumns,
    getAllCommonLabelTemplates,
    getCommonLabelTemplateById,
    deleteCommonLabelTemplates,
    createCommonLabelTemplate,
    updateCommonLabelTemplate,
    processingCommonLabels,
  };
});
